/**
 * Registry tipado de assets do site Consórcio Bazilio.
 *
 * - **R2_BASE**: bucket Cloudflare R2 `bazilio-consorcios` (público via r2.dev subdomain).
 *   Substitui o legado `/manus-storage/*` (que precisava de Forge API e quebrava em dev local).
 *   Pastas no bucket: `logos/<brand>.svg` (SVG monocromáticos via Simple Icons) e `photos/<nome>.jpg` (JPEG ~1600w q85 Unsplash curated).
 *
 * - **Assets locais em `client/public/img/`**: fotos do trio Bazilio + logo SR + materiais administradora parceira
 *   (continuam servidos pelo Vite static, não precisam migrar pois são autênticos do cliente).
 */

export const R2_BASE = "https://pub-b5902f8b67c647b3bc5ed2d709cff7c8.r2.dev";

/** Helper p/ montar URL R2: `r2('photos/vw-tera.jpg')`. */
export const r2 = (path: string) => `${R2_BASE}/${path.replace(/^\/+/, "")}`;

export const ASSETS = {
  /** Logos oficiais Consórcio Bazilio (monograma "B" + telhado/janela).
   *  PNG transparentes já trimados ao bounding box. Full lockup ~866x380, marca ~842x792.
   *  Uso: `light` (dourado, p/ fundo claro) · `dark` (branco/prata, p/ fundo escuro). */
  bazilio: {
    /** Lockup completo dourado "CONSÓRCIO BAZILIO" — fundo claro. */
    logo: "/img/bazilio/logo.png",
    /** Lockup completo branco/prata — fundo escuro. */
    logoWhite: "/img/bazilio/logo-white.png",
    /** Marca (símbolo) dourada — fundo claro. */
    markGold: "/img/bazilio/mark-gold.png",
    /** Marca branca/prata — fundo escuro. */
    markWhite: "/img/bazilio/mark-white.png",
    /** Marca preta sólida — monocromático sobre claro. */
    markBlack: "/img/bazilio/mark-black.png",
  },
  sr: {
    /** Logo full Consórcio Bazilio (color gradients, fundo escuro). */
    logo: "/img/sr/logo.svg",
    /** Só a marca (telhado + monograma SR + onda). */
    logoMark: "/img/sr/logo-mark.svg",
    /** Knockout branca (sobre foto/cor). */
    logoWhite: "/img/sr/logo-white.svg",
  },
  daniela: {
    portrait: "/img/daniela/daniela-portrait.jpeg",
    bw1: "/img/daniela/daniela-bw-1.jpeg",
    bw2: "/img/daniela/daniela-bw-2.jpeg",
    office: "/img/daniela/daniela-office.jpeg",
    extra2: "/img/daniela/daniela-extra-2.jpeg",
  },
  embracon: {
    marcas: "/img/embracon/marcas-administradas.jpeg",
    esteticaParcelada: "/img/embracon/estetica-parcelada.jpeg",
    promo1: "/img/embracon/promo-1.jpeg",
    promo2: "/img/embracon/promo-2.jpeg",
    promo3: "/img/embracon/promo-3.jpeg",
  },
  /** Fotos enviadas pelnossa equipe em conv 29220 (19/06 + 22/06 2026-06).
   *  9 fotos de veículos para os 9 tiers de crédito 100m (R$50k → R$190k).
   *  Originais comprimidos pelo WhatsApp; servidos como JPEG via Vite static. */
  danielaFeedback: {
    vehicles: {
      v50k: "/img/daniela-feedback/vehicles/50k-hb20.jpeg",
      v65k: "/img/daniela-feedback/vehicles/65k-kwid.jpeg",
      v75k: "/img/daniela-feedback/vehicles/75k-onix.jpeg",
      v85k: "/img/daniela-feedback/vehicles/85k-byd-dolphin.jpeg",
      v90k: "/img/daniela-feedback/vehicles/90k-versa.jpeg",
      v100k: "/img/daniela-feedback/vehicles/100k-nivus.jpeg",
      v120k: "/img/daniela-feedback/vehicles/120k-taos.jpeg",
      v150k: "/img/daniela-feedback/vehicles/150k-compass.jpeg",
      v190k: "/img/daniela-feedback/vehicles/190k-rampage.jpeg",
    },
  },
  /** Assets servidos do bucket R2 público. */
  r2: {
    /** Logos SVG monocromáticos (11 brands, Simple Icons CDN). */
    logos: {
      volkswagen: r2("logos/volkswagen.svg"),
      byd: r2("logos/byd.svg"),
      nissan: r2("logos/nissan.svg"),
      renault: r2("logos/renault.svg"),
      honda: r2("logos/honda.svg"),
      yamaha: r2("logos/yamaha-motor.svg"),
      fiat: r2("logos/fiat.svg"),
      jeep: r2("logos/jeep.svg"),
      citroen: r2("logos/citroen.svg"),
      peugeot: r2("logos/peugeot.svg"),
      ducati: r2("logos/ducati.svg"),
    },
    /** Fotos contextuais (Unsplash 1600w q85). */
    photos: {
      vehicles: {
        tera: r2("photos/vw-tera.jpg"),
        belem: r2("photos/vw-belem.jpg"),
        familia: r2("photos/vw-familia.jpg"),
        moto: r2("photos/moto.jpg"),
      },
      morePlans: {
        bydEletrico: r2("photos/byd-eletrico.jpg"),
        vwPolo: r2("photos/vw-polo.jpg"),
        servicosEstetico: r2("photos/servicos-estetico.jpg"),
        creditoServicos: r2("photos/credito-servicos.jpg"),
      },
      properties: {
        imovel1: r2("photos/imovel-1.jpg"),
        imovel2: r2("photos/imovel-2.jpg"),
        imovel3: r2("photos/imovel-3.jpg"),
      },
      agro: {
        rural: r2("photos/agro-rural.jpg"),
      },
      financing: {
        comparativo: r2("photos/comparativo-financiamento.jpg"),
      },
      info: {
        contemplacao1: r2("photos/contemplacao-1-chaves.jpg"),
        contemplacao2: r2("photos/contemplacao-2-familia.jpg"),
        contemplacao3: r2("photos/contemplacao-3-assinatura.jpg"),
        lanceEmbutido: r2("photos/lance-embutido.jpg"),
        lanceLivre: r2("photos/lance-livre.jpg"),
        fgts: r2("photos/fgts-consorcio.jpg"),
        planejamento: r2("photos/info-1-planejamento.jpg"),
        grafico: r2("photos/info-2-grafico.jpg"),
        skyline: r2("photos/info-3-skyline.jpg"),
        meta: r2("photos/info-4-meta.jpg"),
      },
    },
    /** Carros populares BR. Vehicles refactor 2026-06-17. */
    vehiclesBR: {
      polo: r2("vehicles-br/polo-localiza.webp"),
      gol: r2("vehicles-br/gol-localiza.webp"),
      onix: r2("vehicles-br/onix-localiza.webp"),
      cronos: r2("vehicles-br/cronos-localiza.webp"),
      hb20: r2("vehicles-br/hb20-localiza.webp"),
      tcross: r2("vehicles-br/tcross-localiza.webp"),
      compass: r2("vehicles-br/compass-localiza.webp"),
      corolla: r2("vehicles-br/corolla-localiza.webp"),
      strada: r2("vehicles-br/strada-localiza.webp"),
      saveiro: r2("vehicles-br/saveiro-localiza.webp"),
      hilux: r2("vehicles-br/hilux-localiza.webp"),
      renegade: r2("vehicles-br/renegade-localiza.webp"),
    },
    /** Agro BR (cana, soja, gado, colheitadeira, fazenda, trator). */
    agroBR: {
      soja: r2("agro/soja.jpg"),
      cana: r2("agro/cana.jpg"),
      colheitadeira: r2("agro/colheitadeira.jpg"),
      gado: r2("agro/gado.jpg"),
      fazenda: r2("agro/fazenda.jpg"),
      trator: r2("agro/trator.jpg"),
      rural: r2("agro/rural.jpg"),
    },
    /** Headshots gente diversa BR para Testimonials. */
    people: {
      mulher1: r2("people/mulher-1.jpg"),
      mulher2: r2("people/mulher-2.jpg"),
      mulher3: r2("people/mulher-3.jpg"),
      mulher4: r2("people/mulher-4.jpg"),
      homem1: r2("people/homem-1.jpg"),
      homem2: r2("people/homem-2.jpg"),
      homem3: r2("people/homem-3.jpg"),
      homem4: r2("people/homem-4.jpg"),
    },
    /** Sonhos coerentes p/ InfoCarousel Realize Seus Sonhos. */
    dreams: {
      casaPropria: r2("dreams/casa-propria.jpg"),
      carroNovo: r2("dreams/carro-novo-v2.jpg"),
      viagem: r2("dreams/viagem-v2.jpg"),
      fazenda: r2("dreams/fazenda.jpg"),
      moto: r2("dreams/moto.jpg"),
      aposentadoria: r2("dreams/aposentadoria-v2.jpg"),
      faculdade: r2("dreams/faculdade.jpg"),
      reforma: r2("dreams/reforma.jpg"),
    },
    /** Imóveis premium extras p/ Properties refactor. */
    propertiesPremium: {
      fachada: r2("properties-extra/fachada.jpg"),
      aptoVista: r2("properties-extra/apto-vista.jpg"),
      casaPiscina: r2("properties-extra/casa-piscina.jpg"),
      cobertura: r2("properties-extra/cobertura.jpg"),
      premium1: r2("properties-extra/1.jpg"),
      premium2: r2("properties-extra/2.jpg"),
      premium3: r2("properties-extra/3.jpg"),
    },
    /** Pack Bazilio autênticas (FOTOS + IMAGENS promo administradora parceira + LOGO). */
    danielaPack: {
      foto1: r2("daniela-pack/fotos-2026-05-28_10-17-05.jpeg"),
      foto2: r2("daniela-pack/fotos-2026-06-10_21-05-55.jpeg"),
      foto3: r2("daniela-pack/fotos-2026-06-10_21-09-51.jpeg"),
      foto4: r2("daniela-pack/fotos-2026-06-10_21-16-35.jpeg"),
      foto5: r2("daniela-pack/fotos-2026-06-10_21-22-50.jpeg"),
      promo1: r2("daniela-pack/promo-2026-06-10_21-39-06.jpeg"),
      promo2: r2("daniela-pack/promo-2026-06-10_21-39-06-1.jpeg"),
      promo3: r2("daniela-pack/promo-2026-06-10_21-39-07.jpeg"),
      promo4: r2("daniela-pack/promo-2026-06-10_22-52-11.jpeg"),
      promo5: r2("daniela-pack/promo-2026-06-10_22-53-17.jpeg"),
      promo6: r2("daniela-pack/promo-2026-06-10_22-56-52.jpeg"),
      promo7: r2("daniela-pack/promo-2026-06-10_23-38-29.jpeg"),
      logo: r2("daniela-pack/logo-2a2689b23d37f14c98b6.jpeg"),
    },
  },
} as const;

/** Carrossel "Quem é Bazilio". 4 fotos autênticas (1 colorida + 2 PB + 1 escritório). */
export const DANIELA_CAROUSEL = [
  {
    src: ASSETS.daniela.portrait,
    alt: "Trio Bazilio. Especialista em Consórcio do Consórcio Bazilio",
  },
  {
    src: ASSETS.daniela.office,
    alt: "Trio Bazilio no escritório do Consórcio Bazilio em João Monlevade",
  },
  {
    src: ASSETS.daniela.bw1,
    alt: "Retrato profissional de Trio Bazilio (preto e branco)",
  },
  {
    src: ASSETS.daniela.bw2,
    alt: "Trio Bazilio em retrato corporativo",
  },
] as const;
